import { RolesEnum } from '../types/RolesEnum';
import { sortRolesByDisplayOrder } from './specialistRoleModuleHelper';

export const ACTIVE_SPECIALIST_ROLE_SESSION_KEY = 'teccc.activeSpecialistRole';

export const SPECIALIST_ROLE_CARD_CONFIG = {
  administrator: {
    route: '/specialist-flow-cmi',
    badgeKey: 'specialist.selection.roles.administrator.badge',
    badgeFallback: 'Supervisor',
    descriptionKey: 'specialist.selection.roles.administrator',
    descriptionFallback: 'Opera el panel y las bandejas con reglas CIIU, sin quedar como especialista asignado.',
    isOperator: true
  },
  feeder: {
    route: '/specialist-feeder',
    badgeKey: 'specialist.selection.roles.feeder.badge',
    badgeFallback: 'Alimentador',
    descriptionKey: 'specialist.selection.roles.feeder',
    descriptionFallback: 'Asigna términos clave de búsqueda y los envía al Pre-Gestor.',
    isOperator: true
  },
  'pre-manager': {
    route: '/specialist-pre-manager-selection',
    badgeKey: 'specialist.selection.roles.pre-manager.badge',
    badgeFallback: 'Fase 1',
    descriptionKey: 'specialist.selection.roles.pre-manager',
    descriptionFallback: 'Genera el DRI y lo envía a la bandeja de gestores.',
    isOperator: true
  },
  manager: {
    route: '/specialist-manager-selection',
    badgeKey: 'specialist.selection.roles.manager.badge',
    badgeFallback: 'Fase 2',
    descriptionKey: 'specialist.selection.roles.manager',
    descriptionFallback: 'Reclama el DRI de la bandeja, completa el relevamiento de información y lo envía a la bandeja del Verificador.',
    isOperator: true
  },
  validator: {
    route: '/specialist-validator-selection',
    badgeKey: 'specialist.selection.roles.validator.badge',
    badgeFallback: 'Fase 3',
    descriptionKey: 'specialist.selection.roles.validator',
    descriptionFallback: 'Verifica calidad de información de Prospectos de caso entrantes, gestiona licencia de publicación, ajusta el borrador final y envía al editor.',
    isOperator: true
  },
  editor: {
    route: '/specialist-editor-selection',
    badgeKey: 'specialist.selection.roles.editor.badge',
    badgeFallback: 'Fase 4',
    descriptionKey: 'specialist.selection.roles.editor',
    descriptionFallback: 'Edita semántica, estilo y coherencia del texto final del caso y lo publica.',
    isOperator: true
  },
  liaison: {
    route: '/specialist-liaison-selection',
    badgeKey: 'specialist.selection.roles.liaison.badge',
    badgeFallback: 'Fase 5',
    descriptionKey: 'specialist.selection.roles.liaison',
    descriptionFallback: 'Actualiza la información de los casos publicados, pausa o reanuda la visibilidad y da de baja.',
    isOperator: true
  },
  coordinator: {
    route: '/specialist-coordinator-selection',
    badgeKey: 'specialist.selection.roles.coordinator.badge',
    badgeFallback: 'Coordinador',
    descriptionKey: 'specialist.selection.roles.coordinator',
    descriptionFallback: 'Superrol consultivo: ve y modifica lo parametrizado desde la bandeja del Coordinador y el panel.',
    isOperator: true
  }
};

export const normalizeSpecialistRoleKey = (role = {}) => {
  if (role?.roleKey === 'leader') {
    return 'feeder';
  }
  if (role?.roleKey) {
    return role.roleKey;
  }

  const base = (role?.code || role?.name || role?.description || '').toLowerCase();

  if (base.includes('administrator') || base.includes('administrador')) {
    return 'administrator';
  }
  if (base.includes('feeder') || base.includes('visualizador') || base.includes('alimentador') || base.includes('leader') || base.includes('lider') || base.includes('líder')) {
    return 'feeder';
  }
  if (base.includes('pre-manager') || base.includes('pre-gestor') || base.includes('pregestor')) {
    return 'pre-manager';
  }
  if (base.includes('coordinator') || base.includes('coordinador')) {
    return 'coordinator';
  }
  if (base.includes('manager') || base.includes('gestor')) {
    return 'manager';
  }
  if (
    base.includes('verificator')
    || base.includes('verificador')
    || base.includes('validator')
    || base.includes('validador')
  ) {
    return 'validator';
  }
  if (base.includes('liaison') || base.includes('enlace')) {
    return 'liaison';
  }
  if (base.includes('editor')) {
    return 'editor';
  }
  if (base === 'especialista' || base === 'specialist') {
    return 'specialist';
  }

  return null;
};

const ROLE_FLAG_MAP = [
  { flag: 'isSpecialistFeeder', roleProp: 'roleSpecialistFeeder', roleName: RolesEnum.SPECIALIST_FEEDER },
  { flag: 'isSpecialistPreManager', roleProp: 'roleSpecialistPreManager', roleName: RolesEnum.SPECIALIST_PRE_MANAGER },
  { flag: 'isSpecialistManager', roleProp: 'roleSpecialistManager', roleName: RolesEnum.SPECIALIST_MANAGER },
  { flag: 'isSpecialistValidator', roleProp: 'roleSpecialistValidator', roleName: RolesEnum.SPECIALIST_VALIDATOR },
  { flag: 'isSpecialistEditor', roleProp: 'roleSpecialistEditor', roleName: RolesEnum.SPECIALIST_EDITOR },
  { flag: 'isSpecialistCoordinator', roleProp: 'roleSpecialistCoordinator', roleName: RolesEnum.SPECIALIST_COORDINATOR },
  { flag: 'isSpecialistLiaison', roleProp: 'roleSpecialistLiaison', roleName: RolesEnum.SPECIALIST_LIAISON }
];

export const isAdministratorIntention = (roleLogin = {}) => {
  if (!roleLogin?.isAdministrator) {
    return false;
  }
  const selected = roleLogin.roleSpecialistSelected;
  if (!selected?.id) {
    return true;
  }
  return normalizeSpecialistRoleKey(selected) === 'administrator';
};

export const hasFlowInboxOverride = (roleLogin = {}) =>
  normalizeSpecialistRoleKey(roleLogin?.roleSpecialistSelected) === 'coordinator';

export const buildAvailableSpecialistRoles = (roleLogin = {}) => {
  const roles = [];

  ROLE_FLAG_MAP.forEach(({ flag, roleProp, roleName }) => {
    if (!roleLogin[flag]) {
      return;
    }

    const roleData = roleLogin[roleProp];
    if (!roleData?.id) {
      return;
    }

    const roleKey = normalizeSpecialistRoleKey({ name: roleName, ...roleData });
    if (!roleKey) {
      return;
    }

    roles.push({
      ...roleData,
      name: roleData.name || roleName,
      code: roleData.code || roleName.toLowerCase(),
      roleKey,
      card: SPECIALIST_ROLE_CARD_CONFIG[roleKey]
    });
  });

  return sortRolesByDisplayOrder(roles);
};

export const getSpecialistRolePermissions = (roleKey, flowEngineEnabled = false, options = {}) => {
  if (!flowEngineEnabled) {
    return {
      isViewerOnly: false,
      canCreate: roleKey === 'feeder',
      canEdit: roleKey === 'feeder',
      canDelete: roleKey === 'feeder',
      canBulkUpload: roleKey === 'feeder',
      canOperateFlow: false,
      canViewFlow: false
    };
  }

  if (roleKey === 'administrator' || roleKey === 'coordinator') {
    return {
      isViewerOnly: false,
      canCreate: true,
      canEdit: true,
      canDelete: true,
      canBulkUpload: true,
      canOperateFlow: true,
      canViewFlow: true
    };
  }

  if (roleKey === 'liaison') {
    return {
      isViewerOnly: false,
      canCreate: false,
      canEdit: true,
      canDelete: false,
      canBulkUpload: false,
      canOperateFlow: true,
      canViewFlow: true
    };
  }

  if (roleKey === 'feeder') {
    return {
      isViewerOnly: false,
      canCreate: false,
      canEdit: false,
      canDelete: false,
      canBulkUpload: false,
      canOperateFlow: true,
      canViewFlow: true
    };
  }

  if (['pre-manager', 'manager', 'validator', 'editor'].includes(roleKey)) {
    const preManagerEnabled = options.flowPreManagerEnabled !== false;
    return {
      isViewerOnly: false,
      canCreate: roleKey === 'pre-manager' || (roleKey === 'manager' && !preManagerEnabled),
      canEdit: true,
      canDelete: roleKey === 'manager',
      canBulkUpload: roleKey === 'manager' || roleKey === 'pre-manager',
      canOperateFlow: true,
      canViewFlow: true
    };
  }

  return {
    isViewerOnly: false,
    canCreate: false,
    canEdit: false,
    canDelete: false,
    canBulkUpload: false,
    canOperateFlow: false,
    canViewFlow: false
  };
};

export const persistActiveSpecialistRole = (role, userId) => {
  if (!role?.id || !userId || typeof window === 'undefined') {
    return;
  }

  sessionStorage.setItem(
    ACTIVE_SPECIALIST_ROLE_SESSION_KEY,
    JSON.stringify({ userId, roleId: role.id, roleName: role.name })
  );
};

export const readPersistedActiveSpecialistRoleId = (userId) => {
  if (!userId || typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = sessionStorage.getItem(ACTIVE_SPECIALIST_ROLE_SESSION_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (Number(parsed.userId) !== Number(userId)) {
      return null;
    }

    return parsed.roleId ?? null;
  } catch {
    return null;
  }
};

export const clearActiveSpecialistRoleSession = () => {
  if (typeof window === 'undefined') {
    return;
  }

  sessionStorage.removeItem(ACTIVE_SPECIALIST_ROLE_SESSION_KEY);
};

export const resolveModuleAccessForRole = (moduleAccessByRole = {}, role = {}) => {
  const roleId = role?.id;
  if (!roleId) {
    return [];
  }

  const map = moduleAccessByRole || {};
  return map[String(roleId)] || map[Number(roleId)] || [];
};

export const resolveSpecialistRoleForLogin = ({
  roleLogin = {},
  flowEngineEnabled = false,
  userId = null
}) => {
  const availableRoles = buildAvailableSpecialistRoles(roleLogin);

  if (!availableRoles.length) {
    return null;
  }

  if (!flowEngineEnabled) {
    if (availableRoles.length === 1) {
      return availableRoles[0];
    }

    if (roleLogin.isRoleSpecialistSelected && roleLogin.roleSpecialistSelected?.id) {
      return roleLogin.roleSpecialistSelected;
    }

    return availableRoles.find((role) => role.roleKey === 'feeder') || availableRoles[0];
  }

  if (availableRoles.length === 1) {
    return availableRoles[0];
  }

  if (roleLogin.isRoleSpecialistSelected && roleLogin.roleSpecialistSelected?.id) {
    const current = availableRoles.find(
      (role) => Number(role.id) === Number(roleLogin.roleSpecialistSelected.id)
    );
    if (current) {
      return current;
    }
  }

  const persistedRoleId = readPersistedActiveSpecialistRoleId(userId);
  if (persistedRoleId) {
    const persisted = availableRoles.find((role) => Number(role.id) === Number(persistedRoleId));
    if (persisted) {
      return persisted;
    }
  }

  return null;
};
